import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import {
  fetchBookData,
  fetcLitGrpVariationList,
  fetchBookCharsVariationSamples,
  selectBookCharVariationList,
  selectVoiceDetailsValues
} from '../slices/userLitGroupAssetsSlice';
import {
  fetchparseDataFromS3,
  selectParsedStoryData,
} from '../slices/storyEditorSlice';
import { fetchAllBooks } from '../slices/storyEditorSlice';

// Assume these helper functions are imported (adjust the path as needed)
import { sortDialoguesBySpeakerAndEmotion, updateCharDataWithDialogues } from '../utils/sortUpdateDialogues';

export const fetchInitialData = createAsyncThunk(
  'aaiCharSequencer/fetchInitialData',
  async ({ userId, jsonFile }, { dispatch, getState, rejectWithValue }) => {
    let literaryGroup = null;
    let litGroupBook = null;
    try {
      const state = getState();
      const voiceDetailsValues = selectVoiceDetailsValues(state);
      literaryGroup = voiceDetailsValues?.values?.literaryGroup;
      litGroupBook = voiceDetailsValues?.values?.litGroupBook;
    } catch (err) {
      return rejectWithValue(err.message);
    }

    try {
      if (litGroupBook) {
        const props = { userId: userId, reqCategories: "" };
        await dispatch(fetchAllBooks(props)).unwrap();

        try {
          await dispatch(fetchBookData({ bookuuid: litGroupBook })).unwrap();
        } catch (err) {
          console.error("Failed to execute fetchBookData:", err);
        }
        try {
          await dispatch(fetcLitGrpVariationList({ litGroupUuid: literaryGroup })).unwrap();
        } catch (err) {
          console.error("Failed to execute fetcLitGrpVariationList:", err);
        }
        try {
          await dispatch(
            fetchBookCharsVariationSamples({
              litGroupUuid: literaryGroup,
              bookUuid: litGroupBook
            })
          ).unwrap();
        } catch (err) {
          console.error("Failed to execute fetchBookCharsVariationSamples:", err);
        }
        try {
          if (litGroupBook && jsonFile) {
            console.log('fetchInitialData litGroupBook', litGroupBook)
            await dispatch(
              fetchparseDataFromS3({
                userId: userId,
                targetUrl: `${litGroupBook}/book-process-items/${jsonFile}`,
              })
            ).unwrap();
          }
        } catch (err) {
          console.error("Failed to execute fetchparseDataFromS3:", err);
        }
      }

      // Now retrieve the updated parsed story data from state.
      const stateAfter = getState();
      const parsedStoryData = selectParsedStoryData(stateAfter);
      console.log('fetchInitialData', parsedStoryData)
      // Sort dialogues and update character data.
      const sortedDialogues = sortDialoguesBySpeakerAndEmotion(
        parsedStoryData?.dnObj?.dialogueArray
      );
      const bookCharVariationList = selectBookCharVariationList(stateAfter);
      const updatedCharDataArray = updateCharDataWithDialogues(
        bookCharVariationList?.characterList,
        sortedDialogues
      );

      return { success: true, parsedStoryData, sortedDialogues, updatedCharDataArray, bookCharVariationList };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const initialState = {
  // Process/UI state:
  filterValue: "",
  activeElement: null,
  startPlayer: false,
  playerSampleUuid: null,
  isPopupOpen: false,
  error: null,
  bookStats: {
    title: "The Luck of Madonna 13",
    dialogCnt: 0,
    characterCnt: 0,
    totalVariations: 0
  },
  // Accumulated target object:
  targetObj: {
    character: {},
    variation: "",
    speechSample: {},
    voiceSnippet: {},
    textSnippet: {}
  },
  // New properties to store processed data:
  parsedStoryData: {},
  fetchInitialDataStatus: "idle",
  sortedDialogues: [],
  updatedCharDataArray: [],
  bookCharVariationList: []
};

const aaiCharSequencerSlice = createSlice({
  name: "aaiCharSequencer",
  initialState,
  reducers: {
    setFilterValue(state, action) {
      state.filterValue = action.payload;
    },
    setActiveElement(state, action) {
      state.activeElement = action.payload;
    },
    setStartPlayer(state, action) {
      state.startPlayer = action.payload;
    },
    setPlayerSampleUuid(state, action) {
      state.playerSampleUuid = action.payload;
    },
    setIsPopupOpen(state, action) {
      state.isPopupOpen = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setBookStats(state, action) {
      state.bookStats = action.payload;
    },
    resetSequencerState(state) {
      Object.assign(state, initialState);
    },
    setTargetObj(state, action) {
      state.targetObj = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInitialData.pending, (state) => {
        state.fetchInitialDataStatus = 'loading';
        console.log('fetchInitialData', 'loading')
      })
      .addCase(fetchInitialData.fulfilled, (state, action) => {
        state.fetchInitialDataStatus = 'succeeded';
        state.error = null;
        console.log('fetchInitialData', 'succeeded')
        state.parsedStoryData = action.payload.parsedStoryData;
        state.sortedDialogues = action.payload.sortedDialogues;
        state.updatedCharDataArray = action.payload.updatedCharDataArray;
        state.bookCharVariationList = action.payload.bookCharVariationList;
      })
      .addCase(fetchInitialData.rejected, (state, action) => {
        state.error = action.payload;
        state.fetchInitialDataStatus = 'failed';
      });
  }
});

// Export action creators:
export const {
  setFilterValue,
  setActiveElement,
  setStartPlayer,
  setPlayerSampleUuid,
  setIsPopupOpen,
  setError,
  setBookStats,
  resetSequencerState,
  setTargetObj
} = aaiCharSequencerSlice.actions;

const selectAaiCharSequencer = (state) => state.user.aaiCharSequencer;

// Export selectors (adjust paths as needed):
export const selectFilterValue = (state) => state.user.aaiCharSequencer?.filterValue;
export const selectActiveElement = (state) => state.user.aaiCharSequencer?.activeElement;
export const selectStartPlayer = (state) => state.user.aaiCharSequencer?.startPlayer;
export const selectPlayerSampleUuid = (state) => state.user.aaiCharSequencer?.playerSampleUuid;
export const selectIsPopupOpen = (state) => state.user.aaiCharSequencer?.isPopupOpen;
export const selectError = (state) => state.user.aaiCharSequencer?.error;
export const selectBookStats = (state) => state.user.aaiCharSequencer?.bookStats;
export const selectTargetObj = (state) => state.user.aaiCharSequencer?.targetObj;
export const selectParsedStoryDataFromSequencer = (state) => state.user.aaiCharSequencer?.parsedStoryData;
export const selectSortedDialogues = (state) => state.user.aaiCharSequencer?.sortedDialogues;
export const selectUpdatedCharDataArray = (state) => state.user.aaiCharSequencer?.updatedCharDataArray;
export const selectBookCharVariationListFromSequencer = (state) => state.user.aaiCharSequencer?.bookCharVariationList;

export const selectProcessedData = createSelector(
  [selectAaiCharSequencer],
  (aaiCharSequencer) => ({
    parsedStoryData: aaiCharSequencer?.parsedStoryData,
    sortedDialogues: aaiCharSequencer?.sortedDialogues,
    updatedCharDataArray: aaiCharSequencer?.updatedCharDataArray,
    bookCharVariationList: aaiCharSequencer?.bookCharVariationList,
  })
);
export default aaiCharSequencerSlice.reducer;
